import React from "react"
import {
  Box,
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Portal,
  Text,
} from "@chakra-ui/react"
import { Control, FieldValues, useController } from "react-hook-form"
import Icon from "components/elements/Icon"
import { PASS_BUILDER_BACK_TYPE_WIDTH } from "./passBuilderTokens"
import {
  PASS_BACK_SECTION_TYPE_ICONS,
  PASS_BACK_SECTION_TYPE_LABELS,
  PASS_BACK_SECTION_TYPES,
  PassBackSectionType,
} from "./walletPassBuilderTypes"

type OptionLabelProps = {
  type: PassBackSectionType
}

const PassBackSectionTypeOptionLabel = ({ type }: OptionLabelProps) => (
  <HStack spacing={1.5} minW={0}>
    <Icon
      icon={PASS_BACK_SECTION_TYPE_ICONS[type]}
      size={3}
      color="gray.600"
      flexShrink={0}
    />
    <Text as="span" isTruncated>
      {PASS_BACK_SECTION_TYPE_LABELS[type]}
    </Text>
  </HStack>
)

type Props = {
  name: string
  control: Control<FieldValues>
  placeholder?: string
}

const PassBuilderBackSectionTypeSelect = ({
  name,
  control,
  placeholder = "Select Type...",
}: Props) => {
  const { field } = useController({ name, control })
  const selectedType = field.value as PassBackSectionType | ""
  const hasSelection = PASS_BACK_SECTION_TYPES.includes(selectedType as PassBackSectionType)

  return (
    <Menu>
      <MenuButton
        as={Button}
        w={PASS_BUILDER_BACK_TYPE_WIDTH}
        maxW="100%"
        variant="outline"
        bg="white"
        h="40px"
        fontWeight="normal"
        textAlign="left"
        px={3}
        rightIcon={<Icon icon="down-arrow" size={3} />}
        _hover={{ bg: "white" }}
        _active={{ bg: "white" }}
      >
        {hasSelection ? (
          <PassBackSectionTypeOptionLabel type={selectedType as PassBackSectionType} />
        ) : (
          <Text color="gray.700" isTruncated>
            {placeholder}
          </Text>
        )}
      </MenuButton>
      <Portal>
        <MenuList
          zIndex={1500}
          py={1}
          w={PASS_BUILDER_BACK_TYPE_WIDTH}
          minW={PASS_BUILDER_BACK_TYPE_WIDTH}
          maxW={PASS_BUILDER_BACK_TYPE_WIDTH}
        >
          <MenuOptionGroup
            type="radio"
            value={hasSelection ? selectedType : undefined}
            onChange={(value) => field.onChange(value)}
          >
            {PASS_BACK_SECTION_TYPES.map((type) => (
              <MenuItemOption key={type} value={type} icon={null} py={2} px={2}>
                <HStack justify="space-between" w="full" spacing={2}>
                  <PassBackSectionTypeOptionLabel type={type} />
                  <Box w="12px" flexShrink={0} display="flex" justifyContent="flex-end">
                    {selectedType === type && (
                      <Icon icon="checkmark" size={3} color="blue.500" />
                    )}
                  </Box>
                </HStack>
              </MenuItemOption>
            ))}
          </MenuOptionGroup>
        </MenuList>
      </Portal>
    </Menu>
  )
}

export default PassBuilderBackSectionTypeSelect
